import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './0_context/AppContext.js';

import RecordPage from './1_components/1_record/RecordPage.js';
import ReportPage from './1_components/3_report/ReportPage.js';
import SettingsPage from './1_components/5_settings/SettingsPage.js';
import Footer from './1_components/Footer.js';
import MachineGuidePage from './1_components/MachineGuidePage.js';
import { PlayerPage } from './1_components/4_player/PlayerPage.js';

function MainApp() {
  const navigate = useNavigate();
  const {
    currentTab, 
    setCurrentTab, 
    authStatus, 
    handleGoogleLogin, 
    handleLoginSelect, 
    handleLogout, 
    workouts
  } = useAppContext();

  const [noticeData, setNoticeData] = useState(null);
  const [showNotice, setShowNotice] = useState(false);

  useEffect(() => {
    fetch('/notice.json')
      .then(res => res.json())
      .then(data => {
        const hiddenVersion = localStorage.getItem('logup_hide_notice_version');
        if (data && data.version && data.version !== hiddenVersion) {
          setNoticeData(data);
          setShowNotice(true);
        }
      })
      .catch(err => console.log('공지사항 로드 실패:', err));
  }, []);

  const handleHideNotice = () => {
    if (noticeData) {
      try {
        localStorage.setItem('logup_hide_notice_version', noticeData.version);
      } catch (error) {
        console.warn('로컬 스토리지 접근이 제한된 환경입니다.');
      }
    }
    setShowNotice(false);
  };

  return (
    <div className="min-h-screen flex flex-col justify-between">
      <div>
        <header className="max-w-md mx-auto sticky top-0 z-40 bg-white px-5 py-4 border-b flex justify-between items-center shadow-sm">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="LogUP Logo" className="h-9 w-auto object-contain" />
            <div className="flex flex-col justify-center">
              <h1 className="text-lg font-extrabold text-slate-900 leading-tight">
                {currentTab === 'record' && '운동 기록'} 
                {currentTab === 'machine' && '가이드'} 
                {currentTab === 'report' && '성장 리포트'} 
                {currentTab === 'player' && '메인'}
                {currentTab === 'settings' && '설정'}
              </h1>
              <span className="text-[10px] text-indigo-500 font-black uppercase tracking-widest leading-none mt-0.5">LogUP</span>
            </div>
          </div>
          {authStatus === 'google' && (
            <button onClick={handleLogout} className="text-xs font-bold text-rose-500 bg-rose-50 px-3 py-1.5 rounded-xl transition hover:bg-rose-100">
              로그아웃
            </button>
          )}
        </header>

        <main className="max-w-md mx-auto pb-12">
          <Routes>
            <Route path="/" element={
              <>
                <RecordPage isActive={currentTab === 'record'} />
                <ReportPage isActive={currentTab === 'report'} />
                <SettingsPage isActive={currentTab === 'settings'} />
                <PlayerPage 
                  isActive={currentTab === 'player'} 
                  PlayerPageLoginTap={_ => setCurrentTab("settings")} />
                <MachineGuidePage isActive={currentTab === 'machine'} workouts={workouts} setCurrentTab={setCurrentTab} />
              </>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      <Footer />

      {/* 1. 로그인 유도 팝업 */}
      {!authStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60  p-5 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full mx-auto text-center shadow-xl space-y-6">
            <div className="flex flex-col items-center justify-center pt-4">
              <img src="/logo.png" alt="LogUP Logo" className="w-20 h-20 object-contain mb-2" />
              <h2 className="text-xl font-extrabold text-slate-900">LogUP 시작하기</h2>
              <p className="text-xs text-slate-500 font-medium leading-relaxed mb-4">
                💡 로그인을 하시면 클라우드를 통해 운동 데이터가 저장됩니다!
              </p>
            </div>
            
            <div className="space-y-2">
              <button onClick={() => handleGoogleLogin()} className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-md transition duration-200">구글 로그인</button>
              <button onClick={() => handleLoginSelect('guest')} className="w-full py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition duration-200">로그인 하지 않고 내 폰에 기록</button>
            </div>
          </div>
        </div>
      )}

      {/* 버전 업데이트 안내 팝업 */}
      {showNotice && noticeData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60  p-5 animate-fadeIn">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full mx-auto text-center shadow-2xl space-y-5">
            <div className="pt-2">
              <h2 className="text-xl font-extrabold text-indigo-600 mb-4">{noticeData.title}</h2>
              <p className="text-xs text-right p-4 text-gray-400">{noticeData.date}</p>
              <div className="text-sm text-slate-700 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl text-left space-y-2 border border-slate-100">
                {noticeData.content.map((item, index) => (
                  <p key={index}>• {item}</p>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleHideNotice} 
                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-xl font-bold text-[13px] transition duration-200"
              >
                더이상 표시하지 않기
              </button>
              <button 
                onClick={() => setShowNotice(false)} 
                className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-[13px] shadow-md transition duration-200"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 하단 네비게이션 바 */}
      <nav className="max-w-md mx-auto fixed bottom-0 left-0 right-0 h-20 nav-blur px-10 flex justify-between items-center z-40 print-hide">
        <button className={`flex flex-col items-center gap-1 ${currentTab === 'player' ? 'text-indigo-600' : 'text-slate-300'}`} onClick={() => { setCurrentTab('player'); navigate('/'); }}><span className="material-icons-round">emoji_events</span><span className="text-[10px] font-bold">성장</span></button>
        <button className={`nav-item flex flex-col items-center gap-1 ${currentTab === 'record' ? 'text-indigo-600' : 'text-slate-300'}`} onClick={() => { setCurrentTab('record'); navigate('/'); }}><span className="material-icons-round">edit_note</span><span className="text-[10px] font-bold">기록</span></button>
        <button className={`nav-item flex flex-col items-center gap-1 ${currentTab === 'report' ? 'text-indigo-600' : 'text-slate-300'}`} onClick={() => { setCurrentTab('report'); navigate('/'); }}><span className="material-icons-round">insights</span><span className="text-[10px] font-bold">리포트</span></button>
        <button className={`nav-item flex flex-col items-center gap-1 ${currentTab === 'settings' ? 'text-indigo-600' : 'text-slate-300'}`} onClick={() => { setCurrentTab('settings'); navigate('/'); }}><span className="material-icons-round">settings</span><span className="text-[10px] font-bold">설정</span></button>
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Router>
        <MainApp />
      </Router>
    </AppProvider>
  );
}